/**
 * What the message will look like, in both of the forms it is actually delivered as.
 *
 * ## The HTML view
 *
 * Rendered in an iframe with `sandbox=""` — the empty value, which is every restriction on and
 * none lifted: no scripts, no forms, no top-level navigation, no plugins, and an opaque origin
 * so the frame cannot reach this document, its storage or its session. The HTML being previewed
 * is operator-authored, and an operator console is exactly where a template pasted from a vendor
 * gets dropped in without being read first.
 *
 * `srcDoc` rather than a blob URL for the same reason: a blob URL inherits the console's origin,
 * and `sandbox=""` is what takes that back.
 *
 * ## The plain-text view
 *
 * Not decoration. Every real email is two documents, and the `text/plain` alternative is the one
 * a spam filter scores and a screen reader reads. Showing only the HTML half means the operator
 * approves half a message.
 *
 * The server's own text part wins when there is one: the API returns `text` on a template
 * preview, and that is the copy that actually rides in the multipart message. `htmlToPlainText`
 * is the fallback, and it is what makes an **unsaved** draft previewable at all, because the
 * server can only render what has been stored.
 *
 * ## The size chip
 *
 * Gmail clips a message over ~102 kB of markup and hides everything after the cut behind a
 * "view entire message" link, which in a well-built template is the footer and the unsubscribe
 * line. The failure is invisible to whoever wrote it, so the number is on screen before the send
 * rather than in a support ticket after it.
 */
import { useMemo, useState } from 'react';

import type { EmailImage } from '../contracts/types.js';
import { formatBytes, totalBytes } from '../lib/emailAttachments.js';
import { GMAIL_CLIP_BYTES, htmlToPlainText, markupByteLength } from '../lib/emailText.js';
import { IconCode, IconEye } from './icons.js';

type View = 'html' | 'text';

export function EmailPreview({
  subject,
  preheader,
  html,
  text,
  senderName,
  fromEmail,
  images = [],
}: {
  subject: string;
  /** The grey line an inbox prints beside the subject. A real field now, not guesswork. */
  preheader: string;
  html: string;
  /** The server's text part, when this preview came from the API. Derived locally when absent. */
  text?: string | undefined;
  senderName: string;
  fromEmail: string | null;
  images?: readonly EmailImage[];
}): JSX.Element {
  const [view, setView] = useState<View>('html');

  const derived = useMemo(() => htmlToPlainText(html), [html]);
  const plain = text !== undefined && text.trim() !== '' ? text : derived;
  const fromServer = text !== undefined && text.trim() !== '';
  const bytes = useMemo(() => markupByteLength(html), [html]);
  const clipped = bytes > GMAIL_CLIP_BYTES;

  return (
    <div className="mailprev">
      <div className="mailprev__head">
        <div className="mailprev__from">
          <span className="mailprev__avatar" aria-hidden>
            {(senderName.trim().charAt(0) || '?').toUpperCase()}
          </span>
          <span className="mailprev__who">
            <span className="mailprev__name">
              {senderName.trim() === '' ? 'Sender name not set' : senderName}
            </span>
            <span className="mailprev__addr mono">{fromEmail ?? 'from address not set'}</span>
          </span>
        </div>
        <div className="mailprev__subject">
          {subject.trim() === '' ? <span className="faint">No subject</span> : subject}
        </div>
        <div className="mailprev__preheader">
          {preheader.trim() === '' ? (
            <span className="faint">
              No preheader. Inboxes will show the first words of the message instead.
            </span>
          ) : (
            preheader
          )}
        </div>
      </div>

      <div className="mailprev__bar">
        <div className="seg">
          <button
            type="button"
            className={`seg__btn${view === 'html' ? ' seg__btn--on' : ''}`}
            onClick={() => setView('html')}
          >
            <IconEye size={15} /> Rendered
          </button>
          <button
            type="button"
            className={`seg__btn${view === 'text' ? ' seg__btn--on' : ''}`}
            onClick={() => setView('text')}
          >
            <IconCode size={15} /> Plain text
          </button>
        </div>
        <span className="mailprev__spacer" />
        <span className={`mailprev__size${clipped ? ' mailprev__size--over' : ''}`}>
          {formatBytes(bytes)} of markup
        </span>
      </div>

      {clipped && (
        <div className="mailprev__clip" role="alert">
          Over {formatBytes(GMAIL_CLIP_BYTES)}. Gmail will cut the message off and hide the rest,
          footer and unsubscribe link included, behind a link most people do not click. Trim it.
        </div>
      )}

      {view === 'html' ? (
        html.trim() === '' ? (
          <div className="mailprev__empty">Nothing to render yet. Write the message first.</div>
        ) : (
          <iframe
            className="mailprev__frame"
            title="Rendered email preview"
            // Empty sandbox: every restriction on. See this file's header.
            sandbox=""
            srcDoc={html}
          />
        )
      ) : plain === '' ? (
        <div className="mailprev__empty">
          The text version is empty. A message with no readable text scores badly with spam
          filters and reads as blank on a watch.
        </div>
      ) : (
        <>
          <pre className="mailprev__text">{plain}</pre>
          {!fromServer && (
            <div className="mailprev__note faint">
              Worked out from the markup on screen. Saving and previewing shows the server&rsquo;s
              own text part, which is the copy that rides in the message.
            </div>
          )}
        </>
      )}

      {images.length > 0 && (
        <div className="mailprev__attachments">
          <span className="faint">
            {images.length === 1 ? '1 image used' : `${String(images.length)} images used`}
          </span>
          <span className="faint">{formatBytes(totalBytes(images))}</span>
        </div>
      )}
    </div>
  );
}
